import React, {useRef, useState} from 'react';
import {Card} from "primereact/card";
import {Button} from "primereact/button";
import {InputText} from "primereact/inputtext";
import {InputTextarea} from "primereact/inputtextarea";
import {Toast} from "primereact/toast";
import {useNavigate} from "react-router-dom";
import usePost from "../../hooks/usePost";
import {webInfo} from "../../utils/webInfo";
import callToast, {catchError} from "../../utils/helper";

interface Department {
    name: string;
    description: string;
}

interface CompanyForm {
    name: string;
    description: string;
    departments: Department[];
}

const emptyDepartment = {
    name: "",
    description: "",
};

const CreateCompany = () => {
    const navigate = useNavigate();
    const toast = useRef<Toast | null>(null);
    const {post, loading} = usePost();
    const [company, setCompany] = useState<CompanyForm>({
        name: "",
        description: "",
        departments: [{...emptyDepartment}],
    });

    const handleCompanyChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setCompany({
            ...company,
            [event.target.name]: event.target.value,
        });
    }

    const handleDepartmentChange = (
        index: number,
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        const departments = company.departments.map((department, departmentIndex) => {
            if (departmentIndex !== index) return department;

            return {
                ...department,
                [event.target.name]: event.target.value,
            };
        });

        setCompany({
            ...company,
            departments,
        });
    }

    const addDepartment = () => {
        setCompany({
            ...company,
            departments: [...company.departments, {...emptyDepartment}],
        });
    }

    const removeDepartment = (index: number) => {
        setCompany({
            ...company,
            departments: company.departments.filter((_, departmentIndex) => departmentIndex !== index),
        });
    }

    const createCompany = async () => {
        if (!company.name.trim()) {
            callToast(toast, "warn", "Company name is required");
            return;
        }

        const payload = {
            name: company.name,
            description: company.description,
            departments: company.departments.filter((department) => (
                department.name.trim() || department.description.trim()
            )),
        };

        try {
            const url = `${webInfo.baseUrl}/api/admin/companies`;
            const res = await post(url, true, payload);
            if (res.success) {
                callToast(toast, true, "Company created successfully");
                navigate('/companies');
                return;
            }

            callToast(toast, false, res?.error?.message ?? "Failed to create company");
        } catch (e) {
            catchError(e);
            callToast(toast, false, "Failed to create company");
        }
    }

    const header = (
        <div className='flex align-items-center justify-content-between p-4 pb-0'>
            <h4 className="m-0">Create New Company</h4>
            {/*<Button onClick={() => navigate('/companies')} label="Back" icon="pi pi-arrow-left" outlined/>*/}
        </div>
    );

    return (
        <Card style={{maxHeight:'88vh',minHeight:'88vh',overflowY:'auto'}} header={header}>
            <Toast ref={toast}/>
            <div className="grid">
                <div className="col-12 md:col-6">
                    <div className="my-2">
                        <label className="font-medium block">Company Name *</label>
                        <InputText
                            name="name"
                            value={company.name}
                            onChange={handleCompanyChange}
                            className="w-full md:w-10 mt-2"
                            placeholder="Company name"
                        />
                    </div>
                    <div className="my-3">
                        <label className="font-medium">Description</label>
                        <InputTextarea
                            name="description"
                            value={company.description}
                            onChange={handleCompanyChange}
                            className="w-full mt-2"
                            rows={10}
                            autoResize
                            placeholder="Description"
                        />
                    </div>
                </div>
                <div className="col-12 md:col-2"></div>
                <div className="col-12 md:col-4">
                    <div className="flex align-items-center justify-content-between mt-2 mb-3">
                        <h5 className="m-0">Departments</h5>
                        <Button
                            type="button"
                            label="Add New"
                            icon="pi pi-plus"
                            className="p-button-sm"
                            onClick={addDepartment}
                        />
                    </div>

                    {company.departments.map((department, index) => (
                        <Card  key={index} className={'mb-3'}>
                            <div className="grid">
                                <div className="col-12">
                                    <label className="font-medium block">Department Name</label>
                                    <InputText
                                        name="name"
                                        value={department.name}
                                        onChange={(event) => handleDepartmentChange(index, event)}
                                        className="w-full mt-2"
                                        placeholder="Department name"
                                    />
                                </div>
                                <div className="col-12">
                                    <label className="font-medium">Department Description</label>
                                    <InputTextarea
                                        name="description"
                                        value={department.description}
                                        onChange={(event) => handleDepartmentChange(index, event)}
                                        className="w-full mt-2"
                                        rows={2}
                                        autoResize
                                        placeholder="Department description"
                                    />
                                </div>
                            </div>
                            <Button
                                size={'small'}
                                type="button"
                                label="Remove"
                                icon="pi pi-trash"
                                severity="danger"
                                outlined
                                disabled={company.departments.length === 1}
                                onClick={() => removeDepartment(index)}
                            />
                        </Card>
                    ))}
                </div>
            </div>

            <div className="flex justify-content-end gap-2 mt-3">
                <Button
                    label="Cancel"
                    icon="pi pi-times"
                    outlined
                    onClick={() => navigate('/companies')}
                />
                <Button
                    label="Create Company"
                    icon="pi pi-check"
                    loading={loading}
                    onClick={createCompany}
                />
            </div>
        </Card>
    );
};

export default CreateCompany;
